import { lazy, Suspense } from 'react';

import { Link, Navigate, Outlet, useParams, type RouteObject } from 'react-router-dom';

import { Logo, SplashScreen } from '@tankef/ui';

const TandasJoinPage = lazy(() =>
  import('@/domains/savings/tandas/routes').then(m => ({ default: m.TandasJoinPage }))
);
const InviteeAcceptPage = lazy(() =>
  import('@/domains/identity/onboarding/routes').then(m => ({ default: m.InviteeAcceptPage }))
);

/**
 * Redirect del link que el BE manda en emails externos de invitación
 * (`TandaInvitationMailer#external_invitation`):
 *   https://app.tankef.com/invite/tanda/{raw_token}
 *
 * El FE consolida en `/tandas/join/:code` (preview público con accept/decline).
 * Esta ruta sólo traduce el path para no romper links ya enviados por correo.
 */
function InviteToTandaRedirect() {
  const { token } = useParams<{ token: string }>();
  if (!token) return <Navigate to="/tandas" replace />;
  return <Navigate to={`/tandas/join/${encodeURIComponent(token)}`} replace />;
}

/**
 * Layout mínimo para rutas públicas que NO requieren sesión. Header
 * con el logo (lleva a `/login` en lugar de `/dashboard` para no
 * empujar al usuario a una ruta protegida que rebotaría) y un
 * contenedor centrado para el contenido.
 *
 * No incluye sidebar, NotificationsProvider, RealtimeGateProvider ni
 * ningún otro proveedor que requiera sesión activa o token.
 */
function PublicLayout() {
  return (
    <div className="flex min-h-screen flex-col bg-tkf-surface-page">
      <header className="sticky top-0 z-30 border-b border-tkf-line-light bg-tkf-surface">
        <div className="mx-auto flex h-16 max-w-7xl items-center px-4 sm:px-6 lg:px-8">
          <Link to="/login" aria-label="Tankef">
            <Logo isSingle={false} disableLink />
          </Link>
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Rutas verdaderamente públicas: el usuario puede aterrizar acá SIN
 * sesión iniciada (típicamente vía link compartido por WhatsApp /
 * email). El componente decide su propio flujo de auth (e.g. botón
 * "Iniciar sesión para unirme" que redirige al login con `state.from`
 * para volver post-login).
 *
 * Estas rutas se prependen al árbol del router para ganarle en orden
 * de match a `/tandas/:id` (que vive en client-routes y matchearía
 * `:id = "join"` si standalone fuera primero).
 */
export function publicRoutes(): RouteObject[] {
  return [
    {
      element: <PublicLayout />,
      children: [
        {
          path: '/tandas/join/:code',
          element: (
            <Suspense fallback={<SplashScreen />}>
              <TandasJoinPage />
            </Suspense>
          ),
        },
        {
          // Compat con email externo del BE (TandaInvitationMailer):
          // `/invite/tanda/:token` → `/tandas/join/:token`. Sólo redirect.
          path: '/invite/tanda/:token',
          element: <InviteToTandaRedirect />,
        },
        {
          // PM (F7.5): aceptación de invitación a cuenta empresarial. Link de
          // un solo uso; público porque el invitado puede no tener sesión.
          // Más específico (`/invite/tanda/:token`, 3 segmentos) matchea antes.
          path: '/invite/:token',
          element: (
            <Suspense fallback={<SplashScreen />}>
              <InviteeAcceptPage />
            </Suspense>
          ),
        },
      ],
    },
  ];
}
